import { useCallback, useState } from "react";

export interface LFUNode {
  id: number;
  key: string;
  value: string;
  freq: number;
}

export default function LfuEngine(initialCapacity: number) {
  const [capacity, setCapacity] = useState(initialCapacity);
  const [map, setMap] = useState<Map<string, LFUNode>>(new Map());
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  const [highlightNodeId, setHighlightNodeId] = useState<number | null>(null);
  const [highlightFreq, setHighlightFreq] = useState<number | null>(null);
  const [animating, setAnimating] = useState(false);
  const [nextId, setNextId] = useState(1);

  const reset = useCallback((newCapacity?: number) => {
    setMap(new Map());             
    setHighlightKey(null);
    setHighlightNodeId(null);
    setHighlightFreq(null);
    setAnimating(false);
    setNextId(1);          
    
    if (newCapacity !== undefined) {
      setCapacity(newCapacity);
    }
  }, []);

  const computeMinFreq = (m: Map<string, LFUNode>) => {
    if (m.size === 0) return null;
    let min = Infinity;
    for (const n of m.values()) {
      if (n.freq < min) min = n.freq;
    }
    return min;
  };

  const get = useCallback(
    (key: string): string | null => {
      if (!map.has(key)) {
        setHighlightKey(key);
        setHighlightNodeId(null);
        setHighlightFreq(null);
        return null;
      }

      const node = map.get(key)!;

      setAnimating(true);
      setHighlightKey(key);
      setHighlightNodeId(node.id);
      setHighlightFreq(node.freq + 1);

      setMap(prev => {
        const copy = new Map(prev);
        copy.set(key, { ...node, freq: node.freq + 1 });
        return copy;
      });

      setTimeout(() => setAnimating(false), 300);
      return node.value;
    },
    [map]
  );

  const put = useCallback(
    (key: string, value: string) => {
      if (capacity <= 0) return;

      setAnimating(true);
      setHighlightKey(key);

      setMap(prev => {
        const working = new Map(prev);

        // update
        if (working.has(key)) {
          const node = working.get(key)!;
          const updated = { ...node, value, freq: node.freq + 1 };

          working.set(key, updated);
          setHighlightNodeId(updated.id);
          setHighlightFreq(updated.freq);
          return working;
        }

        // evict
        if (working.size >= capacity) {
          const minFreq = computeMinFreq(working);
          for (const [k, n] of working.entries()) {
            if (n.freq === minFreq) {
              working.delete(k);
              break;
            }
          }
        }

        const newNode: LFUNode = {
          id: nextId,
          key,
          value,
          freq: 1,
        };

        working.set(key, newNode);
        setNextId(id => id + 1);
        setHighlightNodeId(newNode.id);
        setHighlightFreq(1);

        return working;
      });

      setTimeout(() => setAnimating(false), 300);
    },
    [capacity, nextId]
  );

  const silentPut = useCallback(
    (key: string, value: string) => {
      if (capacity <= 0) return;

      setMap(prev => {
        const working = new Map(prev);

        if (working.has(key)) {
          const node = working.get(key)!;
          working.set(key, { ...node, value, freq: node.freq + 1 });
          return working;
        }

        if (working.size >= capacity) {
          const minFreq = computeMinFreq(working);
          for (const [k, n] of working.entries()) {
            if (n.freq === minFreq) {
              working.delete(k);
              break;
            }
          }
        }

        working.set(key, {
          id: nextId,
          key,
          value,
          freq: 1,
        });

        setNextId(id => id + 1);
        return working;
      });
    },
    [capacity, nextId]
  );

  return {
    capacity,
    setCapacity,
    map,
    get,
    put,
    silentPut,
    reset,
    highlightKey,
    highlightNodeId,
    highlightFreq,
    animating,
  };
}